import { raw } from '@aha-app/mvc';
import ApplicationModel, {
  type ApplicationModelConstructor,
} from './ApplicationModel';
import ModelCollection from './ModelCollection';
import type Query from './Query';
import { type RelationAttribute } from './RelationAttribute';

export function modelMixin<
  M extends ApplicationModel,
  T extends ApplicationModelConstructor<M>,
  E extends T
>(modelClass: T, mixin: (m: T) => E) {
  return mixin(modelClass);
}

/**
 * Declares decorated `modelClass` as a model type, which can be used to
 * automatically wrap GraphQL objects.
 *
 * Example:
 *
 *   @model
 *   class Something extends ApplicationModel {}
 */
export function model<T extends ApplicationModelConstructor<any>>(
  originalClass: T
) {
  Object.defineProperty(originalClass, 'name', {
    value: originalClass.typename,
  });

  // @ts-expect-error `models` is read-only, but this is where we initialize them all
  ApplicationModel.models[originalClass.typename] = originalClass;
}

/**
 * Wrap `object` in a model object if it can be wrapped.
 */
export function modelInstance<M extends ApplicationModel>(
  object: M | { __typename: string },
  options: { parent?: RelationAttribute } = {}
): M {
  if (object instanceof ApplicationModel) {
    return object;
  } else if (ApplicationModel.models[object.__typename]) {
    const Model = ApplicationModel.models[object.__typename];
    return new Model(object, options);
  } else {
    // We don't have a model defined for __typename, but can still use
    // it as a regular object.
    // @ts-ignore
    return object;
  }
}

interface ModelAttributeOptions {
  parent?: RelationAttribute;
  query?: Query<any, any>;
  json?: boolean;
}

/**
 * Given a value returned from a GraphQL query, return a wrapped
 * version of that value. If you have a bare JS object and want to do
 * something smarter with it, this is the method you want.
 *
 * For example, will wrap models in the correct ApplicationModel
 * subclass, will wrap paginated models in a ModelCollection, will
 * deeply wrap Array and object values, etc.
 *
 * @param attr
 * @param options
 * @param options.parent
 * @param options.query
 */
export function modelAttribute(attr: any, options: ModelAttributeOptions = {}) {
  if (attr === null || attr === undefined) {
    return attr;
  } else if (Array.isArray(attr)) {
    return attr.map(obj => modelAttribute(obj, options));
  } else if (typeof attr === 'object' && attr instanceof ApplicationModel) {
    return attr;
  } else if (typeof attr === 'object' && attr.nodes) {
    return new ModelCollection(attr, options);
  } else if (typeof attr === 'object' && attr.__typename) {
    return modelInstance(attr, options);
  } else if (typeof attr === 'object' && !options.json) {
    return modelObject(attr);
  } else {
    return attr;
  }
}

/**
 * Given an object, wraps all of its values using modelAttribute.
 */
export function modelObject<T>(object, options: ModelAttributeOptions = {}): T {
  const modelObject = {};
  Object.keys(object).forEach(key => {
    modelObject[key] = modelAttribute(object[key], options);
  });
  return modelObject as T;
}

/**
 * Returns an object representing the attributes for a model.
 */
export function generateModelJSON(value: ApplicationModel | any[] | any): any {
  value = raw(value);
  if (value instanceof ApplicationModel) {
    // Serialize all attributes
    const keys = Object.keys(value.attributes);

    const attributes = keys.reduce((serialized, key) => {
      serialized[key] = generateModelJSON(value[key]);
      return serialized;
    }, {});

    return { ...attributes, __typename: value.typename };
  } else if (Array.isArray(value)) {
    return value.map(generateModelJSON);
  } else {
    return value;
  }
}
